import { api } from './api'
import {
  Document,
  DocumentCreateRequest,
  DocumentUpdateRequest,
  DocumentFilter,
  DocumentVersion
} from '../types/document'
import { API_ENDPOINTS } from '../constants/api'

export const documentService = {
  // List documents for a specific case
  listDocuments: async (caseId: number, filters?: Omit<DocumentFilter, 'case_id'>): Promise<Document[]> => {
    const params = new URLSearchParams()
    params.append('case_id', caseId.toString())
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString())
        }
      })
    }
    const query = params.toString()
    return api.get<Document[]>(`${API_ENDPOINTS.DOCUMENTS.BASE}?${query}`)
  },

  // Get a single document by ID
  getDocument: async (id: number): Promise<Document> => {
    return api.get<Document>(API_ENDPOINTS.DOCUMENTS.byId(id))
  },

  // Upload a new document (multipart)
  uploadDocument: async (
    caseId: number,
    title: string,
    description: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<Document> => {
    const formData = new FormData()
    formData.append('case_id', caseId.toString())
    formData.append('title', title)
    formData.append('description', description)
    formData.append('file', file)

    return api.upload<Document>(API_ENDPOINTS.DOCUMENTS.UPLOAD, file, onProgress)
  },

  // Create document metadata (without file upload)
  createDocument: async (data: DocumentCreateRequest): Promise<Document> => {
    return api.post<Document>(API_ENDPOINTS.DOCUMENTS.BASE, data)
  },

  // Update document metadata
  updateDocument: async (id: number, data: DocumentUpdateRequest): Promise<Document> => {
    return api.put<Document>(API_ENDPOINTS.DOCUMENTS.byId(id), data)
  },

  // Delete a document (soft delete)
  deleteDocument: async (id: number): Promise<void> => {
    return api.delete(API_ENDPOINTS.DOCUMENTS.byId(id))
  },

  // Restore a soft-deleted document
  restoreDocument: async (id: number): Promise<Document> => {
    return api.post<Document>(API_ENDPOINTS.DOCUMENTS.restore(id))
  },

  // List versions of a document
  listVersions: async (documentId: number): Promise<DocumentVersion[]> => {
    return api.get<DocumentVersion[]>(API_ENDPOINTS.DOCUMENTS.versions(documentId))
  },

  // Get a specific version
  getVersion: async (documentId: number, version: number): Promise<DocumentVersion> => {
    return api.get<DocumentVersion>(API_ENDPOINTS.DOCUMENTS.versionDetail(documentId, version))
  },

  // Download document file
  downloadDocument: async (documentId: number, version?: number): Promise<Blob> => {
    const url = version
      ? API_ENDPOINTS.DOCUMENTS.downloadVersion(documentId, version)
      : API_ENDPOINTS.DOCUMENTS.download(documentId)
    return api.get<Blob>(url, { responseType: 'blob' })
  }
}